import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Box, Button, Portal, Typography } from "@mui/material";
import { useAtomValue } from "jotai";
import { ortexColorState, userState } from "@/lib/store";
import { withAlpha } from "@/lib/options-platform/theme";
import { saveSettings } from "@/controllers/auth";
import { getTutorial, postTutorialImpression, TutorialStepData } from "@/controllers/tutorials";

/**
 * In-house guided tour (Appcues replacement) — generic and reusable.
 *
 * Content lives in the DB (screening.TutorialStep, served by
 * /interface/api/tutorials/<tourId>); each step optionally names an
 * `anchor`, which must match a `data-tour="<anchor>"` attribute somewhere
 * in the page markup. Anchored steps highlight the target with a
 * spotlight ring (box-shadow cutout) and place the card next to it;
 * anchorless steps (and anchors that aren't currently in the DOM, e.g.
 * hidden on mobile) render as a centered modal, so a tour degrades
 * gracefully on any viewport.
 *
 * Per-user completion is stored via the standard user-settings API under
 * the module_settings key `<tourId>->tour` — `useTour` reads it to decide
 * whether to auto-start, and writes it on any dismissal (skip counts:
 * an abandoned tour must not re-ambush on every visit; it stays one
 * click away behind the page's Help dialog).
 *
 * Usage:
 *   const tour = useTour("options_analyzer");
 *   <TourGuide {...tour.props} />
 *   // restart: tour.start()  (wire into the page Help dialog)
 */

const CARD_W = 430;
const RING_PAD = 6;

/** window CustomEvent used to force-start a tour by id (Help menu,
 *  ?tour= deep links, post-payment messages). */
export const TOUR_START_EVENT = "ortex:start-tour";

// Tour ids with a live useTour instance on the current page. Lets the
// app-wide runner (GlobalTourLauncher) hand a start request to the
// page's own instance instead of double-mounting the same tour.
const mountedTours = new Map<string, number>();

export const isTourMounted = (tourId: string): boolean => (mountedTours.get(tourId) ?? 0) > 0;

/** Force-start a tour from anywhere (replaces window.Appcues.show).
 *  Handled by the page's own useTour instance when one is mounted,
 *  otherwise by GlobalTourLauncher. */
export const startTour = (tourId: string): void => {
    window.dispatchEvent(new CustomEvent(TOUR_START_EVENT, { detail: { tourId } }));
};

// ── Global "turn off all guides" opt-out ──
// Suppresses AUTO-starts of kind="tour" only: explicit starts (Help
// menu, ?tour= links, startTour) and kind="promo" messages are never
// affected. localStorage is the fast local truth (and covers visitors);
// logged-in users also persist it in module_settings ("tours->settings")
// so the choice follows them across devices. A local value wins over
// the login-time settings snapshot in both directions, so toggling
// takes effect immediately.
const TOURS_DISABLED_KEY = "ortex.tours.disabled";

interface UserLike {
    settings?: { module_settings?: unknown };
}

export const areToursDisabled = (user: UserLike | undefined): boolean => {
    try {
        const local = window.localStorage.getItem(TOURS_DISABLED_KEY);
        if (local === "1") return true;
        if (local === "0") return false;
    } catch {
        /* storage unavailable — fall through to settings */
    }
    const settings = user?.settings?.module_settings as Record<string, unknown> | undefined;
    const stored = settings ? (settings["tours->settings"] as { disabled?: boolean } | undefined) : undefined;
    return !!stored?.disabled;
};

export const setToursDisabled = (disabled: boolean, user: UserLike | undefined): void => {
    try {
        window.localStorage.setItem(TOURS_DISABLED_KEY, disabled ? "1" : "0");
    } catch {
        /* private mode etc. */
    }
    if (user?.settings) {
        saveSettings("tours", "settings", { disabled, at: new Date().toISOString() }).catch(() => {
            /* non-fatal — the localStorage flag still holds this browser */
        });
    }
};

/** Standing dismissal for a tour: localStorage first (the Jotai user
 *  settings are a login-time snapshot), honouring the Django-admin
 *  "reset tour" override (an explicit dismissed:false re-arms the tour
 *  across devices unless re-dismissed this session). Shared by
 *  useTour's auto-start and GlobalTourLauncher's route auto-show. */
export const hasTourDismissal = (tourId: string, user: UserLike | undefined): boolean => {
    const settings = user?.settings?.module_settings as Record<string, unknown> | undefined;
    const seen = settings ? (settings[`${tourId}->tour`] as { dismissed?: boolean } | undefined) : undefined;
    const explicitlyReset = seen !== undefined && seen?.dismissed === false;
    let dismissedThisSession = false;
    try {
        dismissedThisSession = window.sessionStorage.getItem(`ortex.tour.${tourId}.dismissed.session`) === "1";
    } catch {
        /* storage unavailable */
    }
    if (explicitlyReset && !dismissedThisSession) {
        try {
            window.localStorage.removeItem(`ortex.tour.${tourId}.dismissed`);
        } catch {
            /* storage unavailable */
        }
        return false;
    }
    try {
        if (window.localStorage.getItem(`ortex.tour.${tourId}.dismissed`) === "1") return true;
    } catch {
        /* storage unavailable — fall through to settings */
    }
    return !!seen?.dismissed;
};

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface TourGuideProps {
    steps: TutorialStepData[];
    open: boolean;
    /** persist=true records "don't show again" for this user+tour. */
    onClose: (persist: boolean) => void;
    /** For impression stats — omit to skip counting. */
    tourId?: string;
}

const TourGuideInner = ({ steps, open, onClose, tourId }: TourGuideProps) => {
    // The colour atom is undefined until user settings hydrate — never
    // feed it raw into style math (that crash took the whole page down).
    const accent = useAtomValue(ortexColorState) || "#0891B2";
    const user = useAtomValue(userState);
    const [idx, setIdx] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);
    // The step whose target has been looked up. Until then nothing is drawn: the card used to open
    // centred and jump to its target a frame later, a layout shift on every page that starts a tour.
    const [locatedStep, setLocatedStep] = useState<unknown>(null);
    // Farewell: one last centered card after "Don't show again" ("tour")
    // or "Turn off all guides" ("all"), telling the user where the
    // guides live if they change their mind.
    const [farewell, setFarewell] = useState<false | "tour" | "all">(false);

    useEffect(() => {
        if (open) {
            setIdx(0);
            setFarewell(false);
        } else {
            // Forgotten on close, so a restart (whose first render still has the old step) waits
            // for its target instead of drawing at the old position and jumping.
            setLocatedStep(null);
            setRect(null);
        }
    }, [open]);

    const step = steps[idx];

    // Impression stats — one ping per step display.
    useEffect(() => {
        if (open && step && tourId) postTutorialImpression(tourId, step.order);
    }, [open, step, tourId]);

    // Locate + measure the step's target: an admin-set CSS selector wins,
    // then the data-tour anchor. Re-measures after the scrollIntoView
    // settles and on window resize.
    useEffect(() => {
        if (!open || !step) return;
        let cancelled = false;
        let el: HTMLElement | null = null;
        if (step.selector) {
            try {
                el = document.querySelector<HTMLElement>(step.selector);
            } catch {
                el = null; // invalid selector typed in admin — centered modal
            }
        }
        if (!el && step.anchor) el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
        if (!el) {
            setRect(null);
            setLocatedStep(step);
            return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const measure = () => {
            if (cancelled) return;
            const r = el.getBoundingClientRect();
            setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        };
        measure();
        setLocatedStep(step);
        const t = setTimeout(measure, 420); // after smooth scroll settles
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            cancelled = true;
            clearTimeout(t);
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [open, step]);

    if (!open || !step || steps.length === 0) return null;
    if (!farewell && locatedStep !== step) return null;

    const isNarrow = typeof window !== "undefined" && window.innerWidth < 700;
    const last = idx === steps.length - 1;

    // Card placement: below the target when there's room, above it
    // otherwise; centered when no target. On narrow (mobile) viewports
    // the card docks to the bottom of the screen instead — anchored
    // popovers get cramped and the thumb is already there.
    const cardSx = (() => {
        const base = {
            position: "fixed" as const,
            zIndex: 1602,
            width: `min(${CARD_W}px, calc(100vw - 24px))`,
        };
        const centered = {
            ...base,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "calc(100vh - 48px)",
        };
        if (!rect || isNarrow) {
            if (isNarrow) return { ...base, left: 12, right: 12, bottom: 12, width: "auto", maxHeight: "calc(100vh - 24px)" };
            return centered;
        }
        // Hard viewport clamping: below the target if ~a card fits there,
        // above it if that side fits, otherwise centered ON TOP of the
        // target — a full-height target (e.g. the whole chart column)
        // used to push the "above" placement past the top edge of the
        // screen. maxHeight + internal scroll guarantees the card can
        // never exceed the viewport no matter how long the copy runs.
        const MIN_CARD = 240;
        const below = rect.top + rect.height + RING_PAD + 12;
        const spaceBelow = window.innerHeight - below - 12;
        const spaceAbove = rect.top - RING_PAD - 24;
        const left = Math.max(12, Math.min(rect.left, window.innerWidth - CARD_W - 12));
        if (spaceBelow >= MIN_CARD) return { ...base, top: below, left, maxHeight: spaceBelow };
        if (spaceAbove >= MIN_CARD) {
            return { ...base, bottom: window.innerHeight - rect.top + RING_PAD + 12, left, maxHeight: spaceAbove };
        }
        return centered;
    })();

    if (farewell) {
        return (
            <Portal>
                <Box sx={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1600 }} />
                <Box
                    sx={{
                        position: "fixed",
                        zIndex: 1602,
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        width: `min(${CARD_W}px, calc(100vw - 24px))`,
                        backgroundColor: "background.default",
                        backgroundImage: `linear-gradient(170deg, ${withAlpha(accent, 0.14)} 0%, transparent 60%)`,
                        border: `1px solid ${withAlpha(accent, 0.5)}`,
                        borderRadius: 2,
                        p: 2.25,
                        boxShadow: 8,
                    }}
                >
                    <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.25, mb: 0.75 }}>{farewell === "all" ? "Got it: all guides are off" : "Got it: we won't show this again"}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {farewell === "all" ? (
                            <>
                                No guide will pop up on its own again. They stay one click away under <b>Help</b> in the menu at the top right: where you can also turn automatic guides back on.
                            </>
                        ) : (
                            <>
                                You can bring this guide back any time from the <b>&#9432; Help</b> icon at the top right of the page. The dotted &#9432; marks around the page give quick reminders as you work.
                            </>
                        )}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.75 }}>
                        <Button size="small" variant="contained" onClick={() => onClose(true)} sx={{ backgroundColor: accent, fontWeight: 700, "&:hover": { backgroundColor: accent, filter: "brightness(1.08)" } }}>
                            Got it
                        </Button>
                    </Box>
                </Box>
            </Portal>
        );
    }

    return (
        <Portal>
            {/* Spotlight: the ring's oversized shadow dims everything except
                the target. With no target, a plain dim layer. */}
            {rect ? (
                <Box
                    sx={{
                        position: "fixed",
                        top: rect.top - RING_PAD,
                        left: rect.left - RING_PAD,
                        width: rect.width + RING_PAD * 2,
                        height: rect.height + RING_PAD * 2,
                        borderRadius: 2,
                        border: `2px solid ${accent}`,
                        boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.6)",
                        zIndex: 1600,
                        pointerEvents: "none",
                        transition: "all 240ms ease",
                    }}
                />
            ) : (
                <Box sx={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1600 }} />
            )}
            {/* Click shield — the page shouldn't react while touring. */}
            <Box sx={{ position: "fixed", inset: 0, zIndex: 1601 }} onClick={(e) => e.stopPropagation()} />

            <Box
                sx={{
                    ...cardSx,
                    backgroundColor: "background.default",
                    backgroundImage: `linear-gradient(170deg, ${withAlpha(accent, 0.14)} 0%, transparent 60%)`,
                    border: `1px solid ${withAlpha(accent, 0.5)}`,
                    borderRadius: 2,
                    p: 2.25,
                    boxShadow: 8,
                    overflowY: "auto",
                }}
            >
                <Typography variant="overline" sx={{ color: accent, fontWeight: 700, letterSpacing: 1 }}>
                    {idx + 1} / {steps.length}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.25, mb: 0.75 }}>{step.title}</Typography>
                <Typography
                    component="div"
                    variant="body2"
                    sx={{
                        opacity: 0.9,
                        lineHeight: 1.5,
                        "& p": { m: 0, mb: 1.25 },
                        "& p:last-child": { mb: 0 },
                        "& ul": { m: 0, mb: 1.25, pl: 2.5 },
                        "& li": { mb: 0.4 },
                        "& li:last-child": { mb: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: step.body }}
                />
                {/* Dots on their own row — sharing a line with four
                    buttons crushed the labels together on a 360px card. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5 }}>
                    {steps.map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: i === idx ? accent : withAlpha(accent, 0.25),
                            }}
                        />
                    ))}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
                    {/* Skip = just this session; "Don't show again" = the
                        permanent opt-out for THIS tour; "Turn off all
                        guides" = the global opt-out (auto-shows only —
                        promos and the Help menu are unaffected). Each
                        permanent opt-out gets a farewell pointing at the
                        Help menu. Done persists too — a finished tour
                        shouldn't re-ambush. */}
                    <Button size="small" onClick={() => setFarewell("tour")} sx={{ opacity: 0.6, whiteSpace: "nowrap", px: 0.75 }}>
                        Don&apos;t show again
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            setToursDisabled(true, user);
                            setFarewell("all");
                        }}
                        sx={{ opacity: 0.6, whiteSpace: "nowrap", px: 0.75 }}
                    >
                        Turn off all guides
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" onClick={() => onClose(false)} sx={{ opacity: 0.7, px: 0.75 }}>
                        Skip
                    </Button>
                    {idx > 0 && (
                        <Button size="small" onClick={() => setIdx((i) => i - 1)}>
                            Back
                        </Button>
                    )}
                    <Button size="small" variant="contained" onClick={() => (last ? onClose(true) : setIdx((i) => i + 1))} sx={{ backgroundColor: accent, fontWeight: 700, px: 2, "&:hover": { backgroundColor: accent, filter: "brightness(1.08)" } }}>
                        {last ? "Done" : "Next"}
                    </Button>
                </Box>
            </Box>
        </Portal>
    );
};

/**
 * Crash isolation: a rendering error inside the tour must dismiss the
 * tour, never take the page down with it (which is exactly what an
 * unhydrated colour atom once did).
 */
const TourGuide = (props: TourGuideProps) => (
    <ErrorBoundary fallbackRender={() => null} onError={(error: Error) => console.error("TourGuide crashed — tour dismissed:", error)}>
        <TourGuideInner {...props} />
    </ErrorBoundary>
);

/** False for signed-out visitors, and until the user has loaded. */
export const isSignedInUser = (user: { subscription_plan?: string } | null | undefined) => !!user?.subscription_plan && user.subscription_plan !== "ORTEX Visitor";

/**
 * Controller hook: fetches the tour's steps, auto-starts it once for
 * users who haven't dismissed it, and persists dismissal.
 */
export function useTour(tourId: string, autoStart = true) {
    const user = useAtomValue(userState);
    const [steps, setSteps] = useState<TutorialStepData[]>([]);
    const [kind, setKind] = useState<string | undefined>(undefined);
    const [open, setOpen] = useState(false);
    const autoStarted = useRef(false);

    useEffect(() => {
        let cancelled = false;
        getTutorial(tourId)
            .then((res) => {
                if (cancelled) return;
                setSteps(res.steps ?? []);
                setKind(res.kind);
            })
            .catch(() => {
                if (!cancelled) setSteps([]);
            });
        return () => {
            cancelled = true;
        };
    }, [tourId]);

    // Auto-start exactly once per browser session, only for signed-in users
    // (promos excepted), and only with no dismissal on record (hasTourDismissal: localStorage first
    // because the Jotai settings are a login-time snapshot, with the
    // Django-admin "reset tour" override honoured). The global "turn off
    // all guides" opt-out suppresses tour auto-starts but never promos —
    // and never explicit starts (start() / the TOUR_START_EVENT
    // listener below).
    useEffect(() => {
        if (!autoStart || autoStarted.current || open || !steps.length) return;
        // Same rule as GlobalTourLauncher: visitors are not ambushed by a tour, and we wait
        // for the user to load so one is never shown in the gap before we know.
        if (kind !== "promo" && !isSignedInUser(user)) return;
        if (kind !== "promo" && areToursDisabled(user)) return;
        if (hasTourDismissal(tourId, user)) return;
        autoStarted.current = true;
        setOpen(true);
    }, [steps, kind, user, open, tourId, autoStart]);

    // Register with the global-runner registry and honour force-start
    // events (Help menu / ?tour= links / startTour()). An explicit start
    // deliberately ignores the dismissal record — it only suppresses
    // auto-starts.
    useEffect(() => {
        mountedTours.set(tourId, (mountedTours.get(tourId) ?? 0) + 1);
        const onStart = (e: Event) => {
            if ((e as CustomEvent).detail?.tourId === tourId) setOpen(true);
        };
        window.addEventListener(TOUR_START_EVENT, onStart);
        return () => {
            const n = (mountedTours.get(tourId) ?? 1) - 1;
            if (n <= 0) mountedTours.delete(tourId);
            else mountedTours.set(tourId, n);
            window.removeEventListener(TOUR_START_EVENT, onStart);
        };
    }, [tourId]);

    const start = useCallback(() => setOpen(true), []);
    const close = useCallback(
        (persist: boolean) => {
            setOpen(false);
            // Skip (persist=false) hides for this visit only; "Don't show
            // again" and Done record the opt-out for this user + tour.
            if (!persist) return;
            try {
                window.localStorage.setItem(`ortex.tour.${tourId}.dismissed`, "1");
                // Session marker so the admin-reset override (above) can't
                // re-arm the tour from the stale settings snapshot after
                // the user just dismissed it in this session.
                window.sessionStorage.setItem(`ortex.tour.${tourId}.dismissed.session`, "1");
            } catch {
                /* private mode etc. */
            }
            if (user?.settings) {
                saveSettings(tourId, "tour", { dismissed: true, at: new Date().toISOString() }).catch(() => {
                    /* non-fatal — the localStorage flag still holds this browser */
                });
            }
        },
        [tourId, user]
    );

    return { steps, open, start, props: { steps, open, onClose: close, tourId } as TourGuideProps };
}

export default TourGuide;
